import { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, useController, Controller } from "react-hook-form";
import CrossIcon from "../../assets/Icons/CrossIcon.svg";
import SaveIcon from "../../assets/Icons/SaveIcon";
import {
  Box,
  Divider,
  Stack,
  Grid,
  TextField,
  Autocomplete,
  Typography,
  MenuItem,
  Button,
} from "@mui/material";
import {
  useAddTransaction,
  useGetEmployeesNotDoneTxs,
  useGetPaymentSource,
  useGetAllTransactionTypes,
} from "pages/Dashboard/dashboardQueries";
import { useGetActiveDevices } from "pages/ManageDevices/deviceQueries";
import { getLocalStorageItem } from "hooks";

const modeOfPayment = [
  "Cash",
  "Debit Card",
  "UPI (Google pay/Phone Pe)",
  "Mobile Wallets",
  "Credit (at the shop)",
  "Credit Card",
  "Bank Transfer",
  "Net Banking",
  "Any other",
];

const AddTransactionFormSchema = yup.object().shape({
  employeeId: yup.string().required("Employee Code is required field."),
  deviceId: yup.string().nullable(true).notRequired(),
  transactionDate: yup
    .date()
    .transform((currentValue, originalValue) => {
      return originalValue.trim() === "" ? undefined : currentValue;
    })
    .required("Transaction Date is a required field.")
    .test(
      "is-valid-date",
      "Transaction Date must be a valid date.",
      function (value) {
        if (value === undefined) return true;
        return !isNaN(value.getTime());
      }
    ),
  type: yup.string().required("Type is a required field."),
  source: yup.string().when("type", (type, schema) => {
    if (type === "NONE") return schema;
    return schema.required("Category is a required field.");
  }),
  description: yup.array().of(yup.string()).when("type", (type, schema) => {
    if (type === "NONE") return schema.nullable(true).notRequired();
    return schema.min(1, "Sub-category is a required field.").required("Sub-category is a required field.");
  }),
  purpose: yup.string().nullable(true).notRequired(),
  amount: yup
    .number()
    .transform((value, originalValue) =>
      originalValue.trim() === "" ? null : value
    )
    .when("type", (type, schema) => {
      if (type === "NONE") return schema.nullable(true).notRequired();
      return schema
        .typeError("Amount must be a number.")
        .min(1, "Amount must be greater than or equal to 1.")
        .required("Amount is a required field.");
    }),
  mode: yup
    .string()
    .transform((value, originalValue) =>
      originalValue.trim() === "" ? null : value
    )
    .when("type", (type, schema) => {
      if (type === "NONE") return schema.nullable(true).notRequired();
      return schema.required("Mode of payment is a required field.");
    }),
  reason: yup.string().when("type", (type, schema) => {
    if (type === "NONE") return schema.required("Reason is a required field.");
    return schema;
  }),
  interest: yup.string().required("Interest is a required field."),
});
const user = getLocalStorageItem("USER_ROLE");

const AddTransactionForm = ({ closeDialog }) => {
  const [paymentSource, setPaymentSource] = useState(null);
  const [searchEmployeeId, setSearchEmployeeId] = useState("");
  const { data: txTypeData } = useGetAllTransactionTypes();
  const txTypes = txTypeData?.data?.filter((t) => t.isActive) ?? [];
  const { data: activeDevicesData } = useGetActiveDevices();
  const activeDevicesList = activeDevicesData?.data ?? [];

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(AddTransactionFormSchema),
    defaultValues: {
      description: [],
      source: "",
      deviceId: "",
      purpose: "",
      interest: "no",
    },
  });
  const { field } = useController({
    name: "employeeId",
    control,
  });
  const { mutate, error: addtxserr } = useAddTransaction();
  const onSubmit = (data) => {
    mutate(
      {
        ...data,
        description: Array.isArray(data.description) ? data.description.filter(Boolean).join(", ") : data.description,
        source: data.source ? Number(data.source) : 0,
        transactionDate: new Date(data.transactionDate).toISOString(),
        purpose: data.purpose || undefined,
        interest: data.interest === "yes",
      },
      { onSuccess: () => closeDialog() }
    );
  };
  useEffect(() => {
    if (addtxserr?.data?.message) {
      setError("employeeId", {
        type: "custom",
        message: addtxserr?.data?.message,
      });
    }
  }, [addtxserr]);

  const { data } = useGetPaymentSource(paymentSource);
  const watchSelectCAtegory = watch("type");
  const watchSelectSource = watch("source");
  const { data: employeesIds } = useGetEmployeesNotDoneTxs({
    search: searchEmployeeId,
  });

  const star = " * ";
  const today = new Date();
  const todayString = today.toISOString().split("T")[0];
  const fourteenDaysAgo = new Date();
  const fortyDaysAgo = new Date();
  fourteenDaysAgo.setDate(today.getDate() - 13);
  fortyDaysAgo.setDate(today.getDate() - 40);
  const fourteenDaysAgoString = fourteenDaysAgo.toISOString().split("T")[0];
  const fortyDaysAgoString = fortyDaysAgo.toISOString().split("T")[0];

  return (
    <div className="w-full h-full bg-white shadow-md rounded-lg">
      <div className="flex justify-between px-[35px] pt-[19px] pb-[15px] ">
        <Typography className="text-center text-[22px] font-semibold tracking-tighter text-[#070707]">
          Add Transaction
        </Typography>
        <div
          className="w-[24px] h-[24px] hover:cursor-pointer"
          onClick={closeDialog}
        >
          <img src={CrossIcon} className="w-[24px] h-[24px]" />
        </div>
      </div>
      <Divider />
      <div className="">
        <form id="login-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column" spacing="24px" className="w-full py-3">
            <Box className="grid grid-cols-2 px-10 items-center">
              <label
                htmlFor=""
                className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
              >
                Employee Code<span className="text-red-500">{star}</span>:
              </label>
              {user === "SUPER_ADMIN" || user === "ADMIN" ? (
                <>
                  <TextField
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "50px",

                        borderRadius: "10px",
                      },
                    }}
                    {...register("employeeId")}
                    type="text"
                    className="rounded-lg"
                    error={Boolean(errors?.employeeId?.message)}
                    helperText={errors?.employeeId?.message}
                  />
                </>
              ) : (
                <Autocomplete
                  disablePortal
                  options={
                    employeesIds?.data?.records?.map((a) => a?.employeeId) || [
                      "",
                    ]
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "50px",

                      borderRadius: "10px",
                    },
                  }}
                  onChange={(event, newValue) => {
                    field.onChange(newValue);
                  }}
                  value={field.value}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        onChange={(e) => setSearchEmployeeId(e.target.value)}
                        label="Employee Id"
                      />
                    );
                  }}
                />
              )}
            </Box>
            <Box className="grid grid-cols-2 px-10 items-center">
              <label
                htmlFor=""
                className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
              >
                Device ID (Optional):
              </label>
              <TextField
                id="outlined-select-device"
                select
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "50px",
                    borderRadius: "10px",
                  },
                }}
                className="rounded-lg"
                {...register("deviceId")}
                error={Boolean(errors?.deviceId?.message)}
                helperText={errors?.deviceId?.message}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {activeDevicesList.map((dev) => (
                  <MenuItem key={dev.id} value={dev.deviceId}>
                    {dev.name} ({dev.deviceId})
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box className="grid grid-cols-2 px-10 items-center">
              <label
                htmlFor=""
                className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
              >
                Interest:
              </label>
              <TextField
                id="outlined-select-interest"
                select
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "50px",
                    borderRadius: "10px",
                  },
                }}
                className="rounded-lg"
                {...register("interest")}
                error={Boolean(errors?.interest?.message)}
                helperText={errors?.interest?.message}
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            </Box>
            <div className="grid grid-cols-2 px-10 items-center">
              <label
                htmlFor=""
                className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
              >
                Transaction Date<span className="text-red-500">{star}</span>:
              </label>
              <input
                max={todayString}
                min={
                  user === "SUPER_ADMIN" || user === "ADMIN"
                    ? fortyDaysAgoString
                    : fourteenDaysAgoString
                }
                // label="Id"
                {...register("transactionDate")}
                type="date"
                className="rounded-lg px-3 h-[50px] border border-[#1B2559]"
                error={Boolean(errors?.transactionDate?.message)}
              />
            </div>
            <Box className="grid grid-cols-2 px-10 items-center">
              <label
                htmlFor=""
                className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
              >
                Type<span className="text-red-500">{star}</span>:
              </label>
              <TextField
                id="outlined-select-currency"
                select
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "50px",
                    borderRadius: "10px",
                  },
                }}
                className="rounded-lg"
                {...register("type")}
                error={Boolean(errors?.type?.message)}
                helperText={errors?.type?.message}
              >
                {txTypes.length > 0 ? (
                  txTypes.map((t) => (
                    <MenuItem
                      key={t.value}
                      value={t.value}
                      onClick={() => setPaymentSource(t.value)}
                    >
                      {t.label}
                    </MenuItem>
                  ))
                ) : (
                  <>
                    <MenuItem value="CREDIT" onClick={() => setPaymentSource("CREDIT")}>
                      INFLOW
                    </MenuItem>
                    <MenuItem value="DEBIT" onClick={() => setPaymentSource("DEBIT")}>
                      OUTFLOW
                    </MenuItem>
                    <MenuItem value="NONE" onClick={() => setPaymentSource("NONE")}>
                      NO TRANSACTIONS
                    </MenuItem>
                  </>
                )}
              </TextField>
            </Box>
            {paymentSource !== "NONE" && (
              <>
                <Box className="grid grid-cols-2 px-10 items-center">
                  <label
                    htmlFor=""
                    className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
                  >
                    Category<span className="text-red-500">{star}</span>:
                  </label>
                  <TextField
                    id="outlined-select-currency"
                    select
                    disabled={watchSelectCAtegory ? false : true}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "50px",
                        borderRadius: "10px",
                      },
                    }}
                    className="rounded-lg"
                    {...register("source")}
                    onChange={(e) => {
                      setValue("source", e.target.value);
                      setValue("description", []);
                    }}
                    error={Boolean(errors?.source?.message)}
                    helperText={errors?.source?.message}
                  >
                    {data?.data?.map((option) => (
                      <MenuItem key={option.id ?? option.name} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box className="grid grid-cols-2 px-10 items-center">
                  <label
                    htmlFor=""
                    className="text-[#1B2559] text-[16px] font-[700] leading-[100%]"
                  >
                    Sub-category<span className="text-red-500">{star}</span>:
                  </label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        id="outlined-select-currency"
                        select
                        disabled={!watchSelectSource}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "50px",
                            borderRadius: "10px",
                          },
                        }}
                        className="rounded-lg"
                        {...field}
                        SelectProps={{
                          multiple: true,
                          renderValue: (selected) => (Array.isArray(selected) ? selected.join(", ") : selected),
                        }}
                        error={Boolean(errors?.description?.message)}
                        helperText={errors?.description?.message}
                      >
                        {(() => {
                          const selectedSource = data?.data?.find((ps) => ps.id === Number(watchSelectSource));
                          const categoryOptions = selectedSource?.category || [];
                          return categoryOptions.map((option) => (
                            <MenuItem key={option.id ?? option} value={option.name ?? option}>
                              {option.name ?? option}
                            </MenuItem>
                          ));
                        })()}
                      </TextField>
                    )}
                  />
                </Box>

                <Box className="grid grid-cols-2 px-10 items-center">
                  <label
                    htmlFor=""
                    className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
                  >
                    Amount<span className="text-red-500">{star}</span>:
                  </label>
                  <TextField
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "50px",

                        borderRadius: "10px",
                      },
                    }}
                    {...register("amount", { min: 1 })}
                    type="number"
                    className="rounded-lg"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      onFocus: (e) => {
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        );
                      },
                    }}
                    error={Boolean(errors?.amount?.message)}
                    helperText={errors?.amount?.message}
                  />
                </Box>
                <Box className="grid grid-cols-2 px-10 items-center">
                  <label
                    htmlFor=""
                    className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
                  >
                    Mode of Payment<span className="text-red-500">{star}</span>:
                  </label>
                  <TextField
                    id="outlined-select-currency"
                    select
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "50px",

                        borderRadius: "10px",
                      },
                    }}
                    className="rounded-lg"
                    {...register("mode")}
                    error={Boolean(
                      errors?.mode?.message && "Payment mode is required"
                    )}
                    helperText={
                      errors?.mode?.message && "Payment mode is required"
                    }
                  >
                    {modeOfPayment.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </>
            )}
            {paymentSource === "NONE" && (
              <Box className="grid grid-cols-2 px-10 items-center">
                <label
                  htmlFor=""
                  className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
                >
                  Reason<span className="text-red-500">{star}</span>:
                </label>
                <TextField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "50px",

                      borderRadius: "10px",
                    },
                  }}
                  className="rounded-lg"
                  {...register("reason")}
                  error={Boolean(errors?.reason?.message)}
                  helperText={errors?.reason?.message}
                />
              </Box>
            )}

            {/* Purpose Field */}
            {paymentSource !== "NONE" && (
              <Box className="grid grid-cols-2 px-10 items-center">
                <label
                  htmlFor=""
                  className="text-[#1B2559] text-[16px]  font-[700] leading-[100%]"
                >
                  Purpose:
                </label>
                <TextField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "50px",
                      borderRadius: "10px",
                    },
                  }}
                  className="rounded-lg"
                  {...register("purpose")}
                  error={Boolean(errors?.purpose?.message)}
                  helperText={errors?.purpose?.message}
                />
              </Box>
            )}
          </Stack>
          <Divider />
          <Box className="flex justify-center py-[24px]">
            <Button
              variant="contained"
              className="capitalize h-[48px] w-[164px]"
              startIcon={<SaveIcon />}
              type="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
          </Box>
        </form>
      </div>
    </div>
  );
};

export default AddTransactionForm;
